import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  ButtonProps,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { v1 as uuidv1 } from 'uuid';

import { useAppSelector } from '../../../../hooks';
import {
  getUsersState,
  IUserGroupTutorRate,
  IUserGroupTutorRateAmount,
  TAdminUpdateUserGroupTutorRates,
  updateUserGroupTutorRatesAsAdmin,
} from '../../../../store/UserSlice';
import {
  getGroupTutorRatesState,
  IGroupTutorRate,
} from '../../../../store/V2/GroupTutorRatesSlice';
import { currencyOptions, TP_COLORS } from '../../../../constants';
import { IGroupTutorRateCourseType } from '../../../../store/V2/GroupTutorRateCourseTypesSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import { IGroupTutorRateVersion } from '../../../../store/V2/GroupTutorRateVersionsSlice';

interface ICustomTutorRateAmount {
  id: string;
  rateVersion: string;
  amount: string;
  currency: string;
  hideToUser: boolean;
  defaultAmount: string;
  defaultCurrency: string;
}

interface ICustomTutorRate {
  id: string;
  tutorCountry: string;
  amounts: ICustomTutorRateAmount[];
  courseType: {
    name: string;
    grades: Array<{ curriculum: string; grade: string }>;
  };
}

export const primaryButtonProps: ButtonProps = {
  disableElevation: true,
  variant: 'contained',
  color: 'secondary',
  sx: {
    color: '#F8FAFC',
    backgroundColor: TP_COLORS.gray.darken,
    fontWeight: '600',
    padding: '6px 10px',
  },
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const GroupTutorRatesSection: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const usersState = useAppSelector(getUsersState);
  const groupTutorRatesState = useAppSelector(getGroupTutorRatesState);

  // State
  const [userRates, setUserRates] = useState<ICustomTutorRate[]>([]);
  const [isLoading, setLoading] = useState(false);

  const userData = usersState.user;

  useEffect(() => {
    setUserRates(
      convertTutorRatesToCustomData(
        groupTutorRatesState.adminUserGroupTutorRates
      )
    );
  }, [groupTutorRatesState.adminUserGroupTutorRates]);

  const convertTutorRatesToCustomData = (tutorRateList: IGroupTutorRate[]) => {
    const list: ICustomTutorRate[] = [];
    const userTutorRateList =
      userData && userData.groupTutorRates && userData.groupTutorRates.length
        ? userData.groupTutorRates
        : [];

    tutorRateList.forEach((tutorRateItem) => {
      const matchingUserTutorRate = userTutorRateList.find(
        (userTutorRate) =>
          userTutorRate.tutorCountry === tutorRateItem.tutorCountry &&
          userTutorRate.courseType ===
            (tutorRateItem.courseType as IGroupTutorRateCourseType).name
      );

      const courseTypeData = tutorRateItem.courseType as IGroupTutorRateCourseType;
      const customTutorRate: ICustomTutorRate = {
        id: uuidv1(),
        tutorCountry: tutorRateItem.tutorCountry,
        courseType: {
          name: courseTypeData.name,
          grades: courseTypeData.grades,
        },
        amounts: tutorRateItem.amounts.map((amountItem) => {
          const tutorRateVersionData = amountItem.rateVersion as IGroupTutorRateVersion;

          const newAmountItem = {
            id: uuidv1(),
            rateVersion: tutorRateVersionData.name,
            amount: '',
            currency: amountItem.currency,
            hideToUser: tutorRateVersionData.hideToUser,
            defaultAmount: amountItem.amount.toString(),
            defaultCurrency: amountItem.currency,
          };

          if (matchingUserTutorRate) {
            const matchingAmount = matchingUserTutorRate.amounts.find(
              (userRateAmountItem) =>
                userRateAmountItem.rateVersion === tutorRateVersionData.name
            );

            if (
              matchingAmount &&
              matchingAmount.currency &&
              matchingAmount.amount
            ) {
              newAmountItem.amount = matchingAmount.amount.toString();
              newAmountItem.currency = matchingAmount.currency;
              newAmountItem.hideToUser = matchingAmount.hideToUser;
            }
          }

          return newAmountItem;
        }),
      };

      list.push(customTutorRate);
    });

    return list;
  };

  const updateAmountData = ({
    id,
    amountId,
    amount,
    currency,
    hideToUser,
  }: {
    id: string;
    amountId: string;
    amount: string;
    currency: string;
    hideToUser: boolean;
  }) => {
    setUserRates((prevState) => {
      const newStateData = [...prevState];

      const index = newStateData.findIndex((item) => item.id === id);

      if (index > -1) {
        const newAmounts = [...newStateData[index].amounts];

        const amountIndex = newAmounts.findIndex(
          (item) => item.id === amountId
        );

        if (amountIndex > -1) {
          newAmounts[amountIndex] = {
            ...newAmounts[amountIndex],
            amount,
            currency,
            hideToUser,
          };
        }

        newStateData[index] = {
          ...newStateData[index],
          amounts: [...newAmounts],
        };
      }

      return [...newStateData];
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (userData) {
      const _tutorRates: IUserGroupTutorRate[] = [];

      userRates.forEach((userRateItem) => {
        const newAmounts: IUserGroupTutorRateAmount[] = [];

        userRateItem.amounts.forEach((amountItem) => {
          const newAmount = {
            rateVersion: amountItem.rateVersion,
            amount: parseFloat(amountItem.amount),
            currency: amountItem.currency,
            hideToUser: amountItem.hideToUser,
          };

          if (newAmount.currency && newAmount.amount && newAmount.amount > 0) {
            newAmounts.push(newAmount);
          }
        });

        const newRateItem = {
          tutorCountry: userRateItem.tutorCountry,
          courseType: userRateItem.courseType.name,
          amounts: newAmounts,
        };

        if (newAmounts.length) {
          _tutorRates.push(newRateItem);
        }
      });

      const apiData: TAdminUpdateUserGroupTutorRates = {
        userId: userData._id,
        groupTutorRates: _tutorRates,
      };

      await dispatch(updateUserGroupTutorRatesAsAdmin(apiData));
    }

    setLoading(false);
  };

  return (
    <Box>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        marginBottom="24px"
      >
        <Typography variant="h6" fontSize="18px">
          Group Class Rates
        </Typography>
        <LoadingButton
          {...primaryButtonProps}
          loading={isLoading}
          onClick={handleSubmit}
        >
          Save
        </LoadingButton>
      </Stack>
      <div>
        <Grid container spacing={3}>
          {userRates.map((rateItem, index) => {
            const amountList = rateItem.amounts;
            const courseType = rateItem.courseType as IGroupTutorRateCourseType;
            const typeName = `${courseType.name}`;

            return [
              <Grid item xs={12} key={`group-rate-item-${index}`}>
                <Typography fontWeight={600} fontSize="14px">
                  {typeName}
                </Typography>
              </Grid>,
              <Grid
                item
                xs={12}
                key={`group-rate-item-fields-continer-${index}`}
              >
                <Grid container spacing={2}>
                  {amountList.map((amountItem, subIndex) => {
                    const _amount = amountItem.amount;
                    const _hideToUser = amountItem.hideToUser;
                    const _currency = {
                      label: amountItem.currency,
                      value: amountItem.currency,
                    };

                    return [
                      <Grid
                        item
                        xs={12}
                        key={`group-rate-item-fields-continer-${index}-heading-${subIndex}`}
                      >
                        <Typography fontWeight={600} fontSize="14px">
                          {amountItem.rateVersion}
                        </Typography>
                      </Grid>,
                      <Grid
                        item
                        xs={12}
                        sm
                        key={`group-rate-item-fields-continer-${index}-amount-${subIndex}`}
                      >
                        <FormControl fullWidth>
                          <TPTextField
                            {...textFieldProps}
                            type="number"
                            label="Amount"
                            placeholder={amountItem.defaultAmount}
                            value={_amount}
                            onChange={(event) =>
                              updateAmountData({
                                id: rateItem.id,
                                amountId: amountItem.id,
                                amount: event.target.value,
                                currency: _currency.value,
                                hideToUser: _hideToUser,
                              })
                            }
                          />
                        </FormControl>
                      </Grid>,
                      <Grid
                        item
                        xs={12}
                        sm
                        key={`group-rate-item-fields-continer-${index}-currency-${subIndex}`}
                      >
                        <FormControl fullWidth>
                          <Autocomplete
                            size="small"
                            disableClearable
                            value={_currency}
                            options={currencyOptions}
                            popupIcon={TPAutoCompleteArrowIconComponent}
                            isOptionEqualToValue={(option, value) =>
                              option.label === value.value
                            }
                            onChange={(event, value) =>
                              updateAmountData({
                                id: rateItem.id,
                                amountId: amountItem.id,
                                amount: _amount,
                                currency: value.value,
                                hideToUser: _hideToUser,
                              })
                            }
                            renderInput={(params) => (
                              <TPTextField
                                {...params}
                                {...textFieldProps}
                                size="small"
                                label="Currency *"
                                placeholder="Currency"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>,
                      <Grid
                        item
                        xs={12}
                        sm
                        key={`group-rate-item-fields-continer-${index}-switch-${subIndex}`}
                      >
                        <FormControlLabel
                          label="Hide to user"
                          control={
                            <Switch
                              checked={_hideToUser}
                              onChange={(event, value) =>
                                updateAmountData({
                                  id: rateItem.id,
                                  amountId: amountItem.id,
                                  amount: _amount,
                                  currency: _currency.value,
                                  hideToUser: value,
                                })
                              }
                            />
                          }
                        />
                      </Grid>,
                    ];
                  })}
                  {!userRates.length &&
                  groupTutorRatesState.status !== 'loading' ? (
                    <Grid item xs={12}>
                      <Typography>No group rates are available</Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>,
            ];
          })}
        </Grid>
      </div>
    </Box>
  );
};

export default GroupTutorRatesSection;
